import { Ban, BookMarked, CheckCircle2, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { InventoryItemTypeModal } from '../components/InventoryItemTypeModal';
import { inventoryItemTypesApi } from '../inventoryApi';
import type { InventoryItemTypeListItem } from '../inventoryTypes';

type TabKey = 'info';

/** One governed item name. */
export function InventoryItemTypeProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [itemType, setItemType] = useState<InventoryItemTypeListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      inventoryItemTypesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setItemType(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this item type.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await inventoryItemTypesApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this item type.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Logistics',
    'Inventory',
    { label: 'Settings', to: '/inventory/settings' },
    { label: 'Item Types', to: '/inventory/settings/item-types' },
    itemType?.name ?? 'Item type',
  ];

  if (error && !itemType) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !itemType) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { itemCount } = itemType;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="inventory-item-type-profile"
        header={
          <ProfileHeader
            id="inventory-item-type-profile-header"
            icon={<BookMarked className="h-6 w-6" aria-hidden="true" />}
            title={itemType.name}
            meta={[
              itemType.inventoryCategoryName ?? 'Offered under every category',
              `${itemCount} ${itemCount === 1 ? 'item carries' : 'items carry'} it`,
            ]}
            status={
              <StatusText tone={itemType.isActive ? 'active' : 'inactive'}>
                {itemType.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={itemType.createdByName}
            createdAt={itemType.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="inventory-item-type-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : itemType.isActive ? (
            <ProfileBanner
              id="inventory-item-type-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This name is offered on the item form. Every item drawn from it resolves the name on every read, so correcting the word here corrects all of them."
            />
          ) : (
            <ProfileBanner
              id="inventory-item-type-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This item type is deleted. It is no longer offered, and the items already drawn from it keep resolving its name."
              action={
                <PermissionGate permission="inventory-item-types.delete">
                  <Button
                    id="inventory-item-type-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="inventory-item-type-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'inventory-item-types.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: itemType.name },
              { label: 'Description', value: itemType.description },
              {
                label: 'Offered under',
                value:
                  itemType.inventoryCategoryName ??
                  'Every category — the deliberate default, for a name that spans them',
              },
              {
                label: 'Items carrying it',
                value: (
                  <span className={`font-mono tabular-nums ${itemCount === 0 ? 'text-slate-300' : ''}`}>
                    {itemCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="inventory-item-type-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: itemType.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(itemType.createdAt)} className="font-mono">
                    {formatDateTime(itemType.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: itemType.updatedByName },
              {
                label: 'Updated',
                value: itemType.updatedAt ? (
                  <time dateTime={toIso(itemType.updatedAt)} className="font-mono">
                    {formatDateTime(itemType.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <InventoryItemTypeModal
          open
          editing={itemType}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
